/**
 * Wire protocol between the SessionRoom Durable Object and every client kind
 * (player phone, facilitator console, big screen, viewer). JSON over WebSocket.
 */
import type { AudienceSummary, ParticipantContext } from "./participants";
import type { DecisionRecord, RunState } from "./scenario/engine";
import type { OptionQuality, Scenario } from "./scenario/schema";
import type { SimulationPace, SimulationTemperament } from "./simulation";

export type ClientKind = "player" | "facilitator" | "screen" | "viewer";
export type SessionStatus = "lobby" | "live" | "debrief" | "ended";

/** Signed, opaque-to-the-client join credential carried in the WebSocket URL. */
export interface JoinClaims {
  /** Session id (D1 primary key). */
  sid: string;
  /** Session code (6 chars). */
  code: string;
  /** Participant id (stable across reconnects). */
  pid: string;
  kind: ClientKind;
  /** Unix seconds. */
  exp: number;
}

export interface PublicPlayer {
  id: string;
  name: string;
  roleId: string | null;
  connected: boolean;
  score: number;
  streak: number;
  votes: number;
  badges: string[];
  /** Emoji avatar chosen at join. */
  avatar: string;
  /**
   * An artificial participant the facilitator added to demonstrate the exercise. Always shown as
   * such; never carries participant context. Reports written before this existed lack the field.
   */
  simulated?: boolean;
}

export interface VoteWindow {
  nodeId: string;
  openedAt: number;
  /** Unix ms when the window closes automatically. */
  closesAt: number;
  /** Total seconds the window was given (after extensions). */
  durationSec: number;
  /** optionId → live count (only sent when the room is configured to show live tallies). */
  tally: Record<string, number> | null;
  votesCast: number;
  eligible: number;
  /** Set once closed and revealed. */
  result: DecisionRecord | null;
  /** Player ids that already voted (so screens can show progress without revealing choices). */
  votedPlayerIds: string[];
}

export interface PollWindow {
  nodeId: string;
  openedAt: number;
  closesAt: number;
  tally: Record<string, number>;
  votesCast: number;
  closed: boolean;
}

export interface Reaction {
  playerId: string;
  emoji: string;
  at: number;
}

export interface SessionSettings {
  /** Show live vote counts while the vote is open (Kahoot-style) or only at reveal. */
  liveTally: boolean;
  /** Allow read-only viewers to join via the watch link. */
  allowViewers: boolean;
  /** Allow players to pick their own role in the lobby. */
  openRoleSelection: boolean;
  /** Auto-advance linear nodes after this many seconds (0 = manual). */
  autoAdvanceSec: number;
  /** Locale used for scenario content on the big screen. Players choose their own UI locale. */
  locale: string;
  /** Show the leaderboard between decisions. */
  showLeaderboard: boolean;
  /** Emoji reactions from players appear on the big screen. */
  reactions: boolean;
  /**
   * Show the projector's sidebar (join code, players, leaderboard, readiness) beside the stage once the
   * session is live. Off gives the stage the full width. The lobby never shows it: its main view already
   * carries the join details at room size.
   */
  showSidebar: boolean;
}

export const DEFAULT_SETTINGS: SessionSettings = {
  liveTally: false,
  allowViewers: true,
  openRoleSelection: true,
  autoAdvanceSec: 0,
  locale: "en",
  showLeaderboard: true,
  reactions: true,
  showSidebar: true,
};

/** State every client receives. Kept small — it is broadcast on every change. */
export interface PublicState {
  sessionId: string;
  code: string;
  title: string;
  status: SessionStatus;
  scenarioId: string;
  scenarioVersion: string;
  settings: SessionSettings;
  run: RunState | null;
  /** Current node id (duplicate of run.nodeId for convenience; null in lobby). */
  nodeId: string | null;
  /** Which transcript line the facilitator has revealed so far on a call node. */
  revealedLines: number;
  vote: VoteWindow | null;
  poll: PollWindow | null;
  /** Top players by score (capped for non-facilitator sockets; facilitators get everyone). */
  players: PublicPlayer[];
  playerCount: number;
  /** How many of `playerCount` are simulated participants. */
  simulatedCount: number;
  viewerCount: number;
  screenCount: number;
  facilitatorConnected: boolean;
  startedAt: number | null;
  endedAt: number | null;
  /** Server time when the snapshot was produced (clients compute clock skew from it). */
  serverNow: number;
  /** Team dimension scores (null until exercised). */
  dimensionScores: Record<string, number | null>;
  readiness: number | null;
  teamBadges: string[];
  /** Ids of injects available right now. */
  availableInjects: string[];
  /** Whether the current linear node has been "advanced past" its reveal (for call nodes: all lines shown). */
  paused: boolean;
  /** Increments on every state change; clients ignore stale snapshots. */
  version: number;
}

/** Private, per-connection data. */
export interface PrivateState {
  playerId: string;
  kind: ClientKind;
  roleId: string | null;
  name: string;
  /** The option this player chose in the open vote, if any. */
  myVote: string | null;
  myPollVote: string | null;
  score: number;
  lastPoints: { points: number; breakdown: Record<string, number>; quality: OptionQuality } | null;
  badges: string[];
  /** Private role brief + role note for the current node. */
  roleNote: string | null;
}

/* ---------------------------------- client → server ---------------------------------- */

export type FacilitatorCommand =
  | { action: "start" }
  | { action: "next" }
  | { action: "back" }
  | { action: "goto"; nodeId: string }
  | { action: "revealLine" }
  | { action: "revealAllLines" }
  | { action: "openVote" }
  | { action: "closeVote" }
  | { action: "extendVote"; seconds: number }
  | { action: "forceOutcome"; optionId: string }
  | { action: "openPoll" }
  | { action: "closePoll" }
  | { action: "inject"; injectId: string }
  | { action: "assignRole"; playerId: string; roleId: string | null }
  | { action: "renamePlayer"; playerId: string; name: string }
  | { action: "kick"; playerId: string }
  | { action: "updateSettings"; settings: Partial<SessionSettings> }
  | { action: "pause"; paused: boolean }
  | { action: "end" }
  | { action: "reopenLobby" }
  /** Fill roles with artificial participants that vote, answer polls and react on their own. */
  | {
      action: "addSimulated";
      count?: number;
      temperament?: SimulationTemperament;
      pace?: SimulationPace;
      seed?: number;
    }
  | { action: "removeSimulated" };

export type ClientMessage =
  | { type: "vote"; nodeId: string; optionId: string }
  | { type: "pollVote"; nodeId: string; optionId: string }
  | { type: "react"; emoji: string }
  | { type: "chooseRole"; roleId: string | null }
  | { type: "setName"; name: string; avatar?: string }
  | { type: "cmd"; cmd: FacilitatorCommand }
  | { type: "ping"; t: number };

/* ---------------------------------- server → client ---------------------------------- */

export type ServerEvent =
  | { type: "reaction"; reaction: Reaction }
  | { type: "voteAck"; nodeId: string; optionId: string }
  /** Sent instead of a full state when live tallies are hidden; `voterIds` are the players who just voted. */
  | { type: "votesProgress"; votesCast: number; eligible: number; voterIds: string[] }
  | { type: "revealed"; nodeId: string; optionId: string; quality: OptionQuality }
  | { type: "playerJoined"; player: PublicPlayer }
  | { type: "playerLeft"; playerId: string }
  | {
      type: "toast";
      level: "info" | "success" | "warning" | "error";
      key: string;
      params?: Record<string, string | number>;
    }
  | { type: "kicked" }
  | { type: "ended" };

export type ServerMessage =
  | { type: "snapshot"; state: PublicState; me: PrivateState }
  | { type: "state"; state: PublicState }
  | { type: "me"; me: PrivateState }
  | { type: "event"; event: ServerEvent }
  | { type: "pong"; t: number; serverNow: number }
  | { type: "error"; code: string; message: string };

/* ---------------------------------- REST payloads ---------------------------------- */

export interface JoinInfo {
  code: string;
  title: string;
  status: SessionStatus;
  scenario: { id: string; title: string; tagline: string; estimatedMinutes: number };
  roles: { id: string; name: string; icon: string; taken: number; max: number | null }[];
  settings: Pick<SessionSettings, "openRoleSelection" | "allowViewers" | "locale">;
  playerCount: number;
  /** Present when a seat token was supplied. */
  seat?: { label: string; roleId: string | null; hasEmail: boolean };
}

export interface JoinResponse {
  playerId: string;
  token: string;
  kind: ClientKind;
  roleId: string | null;
  name: string;
  wsPath: string;
}

export interface SessionRecord {
  id: string;
  code: string;
  title: string;
  scenarioId: string;
  scenarioVersion: string;
  status: SessionStatus;
  locale: string;
  createdAt: number;
  startedAt: number | null;
  endedAt: number | null;
  playerCount: number;
  readiness: number | null;
  settings: SessionSettings;
}

export interface Seat {
  id: string;
  label: string;
  /** Optional address the facilitator reserved the seat with. Shown masked, never sent anywhere. */
  email: string | null;
  roleId: string | null;
  token: string;
  claimedBy: string | null;
  createdAt: number;
}

/**
 * One participant as the after-action report describes them: their public play record plus the
 * coarse context the edge captured (city/region/country, network operator, device, masked email).
 * A superset of {@link PublicPlayer} — reports written before this existed simply lack the extras.
 */
export interface ReportParticipant extends PublicPlayer {
  kind: "player" | "viewer";
  /** Unix ms when they first joined. */
  joinedAt: number;
  /** Unix ms of their last activity on the socket. */
  lastSeenAt: number;
  /** Reserved-seat label, when they joined through a per-person link. */
  seatLabel: string | null;
  /** How many of the decisions they were present for they actually voted on. */
  votesCast: number;
  votesPossible: number;
  context: ParticipantContext;
}

/** After-action report, persisted at session end. */
export interface AfterActionReport {
  sessionId: string;
  code: string;
  title: string;
  scenario: Pick<
    Scenario,
    | "id"
    | "version"
    | "title"
    | "tagline"
    | "summary"
    | "dimensions"
    | "roles"
    | "badges"
    | "learningObjectives"
    | "basedOn"
  > & {
    /** Present when the scenario was a facilitator's fork: what it was based on, for the provenance line. */
    forkedFrom?: { sourceId: string; sourceTitle: string; sourceVersion: string };
  };
  locale: string;
  startedAt: number;
  endedAt: number;
  durationMs: number;
  readiness: number | null;
  dimensionScores: Record<string, number | null>;
  teamBadges: string[];
  run: RunState;
  decisions: (DecisionRecord & {
    title: string;
    prompt: string;
    options: { id: string; label: string; quality: OptionQuality; feedback: string; votes: number }[];
  })[];
  polls: {
    nodeId: string;
    title: string;
    prompt: string;
    tally: Record<string, number>;
    options: { id: string; label: string }[];
  }[];
  injectsFired: { id: string; title: string }[];
  callsReviewed: { nodeId: string; title: string; redFlags: string[]; tells: string[] }[];
  /** Players, best score first. */
  players: ReportParticipant[];
  /** Read-only viewers who watched the exercise. */
  observers?: ReportParticipant[];
  /** Aggregate view of where the room joined from. */
  audience?: AudienceSummary;
  timeline: { at: number; type: string; detail: string }[];
  recommendations: string[];
}

export const SESSION_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const SESSION_CODE_LENGTH = 6;
export const MAX_PLAYERS_PER_SESSION = 500;
export const MAX_NAME_LENGTH = 24;
export const REACTION_EMOJI = ["👏", "😬", "🤔", "🚩", "✅", "🔥"] as const;
export const AVATARS = [
  "🦊",
  "🐼",
  "🦉",
  "🐙",
  "🦁",
  "🐧",
  "🦄",
  "🐢",
  "🦋",
  "🐝",
  "🐬",
  "🦜",
  "🐺",
  "🦔",
  "🐸",
  "🦩",
] as const;

export function isSessionCode(s: string): boolean {
  return new RegExp(`^[${SESSION_CODE_ALPHABET}]{${SESSION_CODE_LENGTH}}$`).test(s);
}
